/**
 * GPXUSAP-1860 — pure, framework-free core for the "admin uploads on behalf of a
 * vendor while impersonating" flow.
 *
 * Kept deliberately dependency-free (no Angular, no RxJS) so it can be unit
 * tested with vitest even though the rest of this app's `ng test` harness is
 * broken. `_app-core/services/impersonation/impersonation.service.ts` is the
 * thin Angular wrapper around these functions — do not duplicate this logic
 * there.
 */

const TOKEN_SEPARATOR = "$$$";

export interface ParsedImpersonationToken {
    sessionId: string | undefined;
    vendorCode: string | undefined;
    adminUser: string | undefined;
    adminEmail: string | undefined;
    sellerCode: string | undefined;
}

/** Reads localStorage `impersonation` defensively; never throws. */
export function isImpersonated(store: Pick<Storage, "getItem">): boolean {
    try {
        return store?.getItem?.("impersonation") === "true";
    } catch {
        return false;
    }
}

/**
 * True only when the current session is impersonated AND the seller's
 * `uiConfig.adminUploadOnBehalf` flag is strictly boolean `true`. Any other
 * value (absent, `undefined`, the string `"true"`, `false`, garbage) is
 * treated as OFF — this keeps the feature default-OFF and immune to
 * stringly-typed localStorage values.
 */
export function canSubmitWhileImpersonated(
    store: Pick<Storage, "getItem">,
    uiFlag: unknown
): boolean {
    return isImpersonated(store) && uiFlag === true;
}

/**
 * Parses the `$$$`-joined impersonation token positionally, reading ONLY the
 * five identity fields at their fixed indices (0-4). Never throws —
 * malformed, empty, null, or undefined input yields a result with
 * `undefined` fields rather than an exception.
 *
 * GPXUSAP-1860 fix-round-1: this function does NOT interpret any trailing
 * segment as a "landing" hint. It previously did (treating segment index 5
 * as an optional landing value on a 6-segment token), but that collided with
 * today's production token shape, where index 5 is ALWAYS `username` (the
 * full shape is sessionId, code, adminName, adminEmail, sellerCode,
 * username, sessionTimeout, expiryDate, lastLoginDate — 9 segments). A
 * trailing landing segment on a 5-segment token would have landed exactly on
 * `username` and been posted to the backend as
 * `reqData.username: "purchase-orders"`. The landing hint is now carried as
 * a separate `landing` query parameter by the caller (see
 * `impersonate.component.ts`), never as part of this token, so this
 * function stays purely positional regardless of token length (5, 6, 9, or
 * any other segment count all yield identical first-five-field values).
 */
export function parseImpersonationToken(token: string): ParsedImpersonationToken {
    const empty: ParsedImpersonationToken = {
        sessionId: undefined,
        vendorCode: undefined,
        adminUser: undefined,
        adminEmail: undefined,
        sellerCode: undefined,
    };

    if (typeof token !== "string" || token.length === 0) {
        return empty;
    }

    let parts: string[];
    try {
        parts = token.split(TOKEN_SEPARATOR);
    } catch {
        return empty;
    }

    return {
        sessionId: parts[0] || undefined,
        vendorCode: parts[1] || undefined,
        adminUser: parts[2] || undefined,
        adminEmail: parts[3] || undefined,
        sellerCode: parts[4] || undefined,
    };
}
